<template>
  <!-- Renders nothing when the shop has never bought SMS credits.
       Backend signals this with `autoRecharge === null` (no `shops_credits`
       row of type smsV4). The widget only appears once the user has at
       least one credit purchase under their belt. -->
  <section
    v-if="autoRecharge"
    class="auto-recharge-block"
    :class="{ 'auto-recharge-block--on': autoRecharge.enabled }"
  >
    <header class="auto-recharge-block__head">
      <div class="auto-recharge-block__icon">
        <i class="fa-regular fa-arrows-rotate" />
      </div>
      <div class="auto-recharge-block__text">
        <h3 class="auto-recharge-block__title">
          {{ t('billing.autoRechargeBlock.title') }}
        </h3>
        <p class="auto-recharge-block__subtitle">
          {{ autoRecharge.enabled
            ? t('billing.autoRechargeBlock.subtitleOn')
            : t('billing.autoRechargeBlock.subtitleOff') }}
        </p>
      </div>
      <InputSwitch
        :model-value="autoRecharge.enabled"
        :disabled="submitting"
        @update:model-value="onToggle"
      />
    </header>

    <!-- Summary visible only when enabled. Read-only; edit goes through the
         pencil icon → form dialog. -->
    <div
      v-if="autoRecharge.enabled"
      class="auto-recharge-block__summary"
    >
      <i class="fa-regular fa-circle-info auto-recharge-block__summary-icon" />
      <div class="auto-recharge-block__summary-text">
        <template v-if="hasConfig">
          {{ t('billing.autoRechargeBlock.summary', {
            threshold: formatAmount(autoRecharge.threshold ?? 0),
            amount: formatAmount(autoRecharge.rechargeAmount ?? 0),
          }) }}
        </template>
        <template v-else>
          {{ t('billing.autoRechargeBlock.summaryUnconfigured') }}
        </template>
      </div>
      <button
        type="button"
        class="auto-recharge-block__edit"
        :disabled="submitting"
        :aria-label="t('billing.autoRechargeBlock.editAriaLabel')"
        @click="openEditForm"
      >
        <i class="fa-regular fa-pen-to-square" />
      </button>
    </div>

    <!-- Form dialog : used both for first-activation and for edit. -->
    <AutoRechargeFormDialog
      v-model:visible="formVisible"
      :mode="formMode"
      :initial-threshold="autoRecharge.threshold"
      :initial-recharge-amount="autoRecharge.rechargeAmount"
      :currency="currency"
      :submitting="submitting"
      :error-message="formError"
      @confirm="onFormConfirm"
      @cancel="onFormCancel"
    />

    <!-- Confirmation dialog : used for disabling. -->
    <BillingConfirmDialog
      v-model:visible="disableConfirmVisible"
      :title="t('billing.autoRechargeBlock.disableConfirm.title')"
      :message="t('billing.autoRechargeBlock.disableConfirm.message')"
      :confirm-label="t('billing.autoRechargeBlock.disableConfirm.confirm')"
      :cancel-label="t('billing.autoRechargeBlock.disableConfirm.cancel')"
      variant="danger"
      :submitting="submitting"
      @confirm="onDisableConfirm"
    />
  </section>
</template>

<script lang="ts">
import {
  computed, defineComponent, PropType, ref,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from '@/store';
import InputSwitch from 'primevue/inputswitch';
import AutoRechargeFormDialog, {
  AutoRechargeFormMode,
  AutoRechargeFormValue,
} from '@/components/billing/dialogs/AutoRechargeFormDialog.vue';
import BillingConfirmDialog from '@/components/billing/shared/BillingConfirmDialog.vue';
import { setWalletAutoRecharge } from '@/composables/billing/billingApi';
import { WalletAutoRecharge } from '@/types/billing-types';
import { showToastError } from '@/helpers';
import { formatBillingAmount } from '@/composables/billing/formatAmount';

export default defineComponent({
  name: 'AutoRechargeBlock',

  components: {
    InputSwitch,
    AutoRechargeFormDialog,
    BillingConfirmDialog,
  },

  props: {
    autoRecharge: {
      type: Object as PropType<WalletAutoRecharge | null>,
      default: null,
    },
  },

  setup(props) {
    const { t } = useI18n();
    const store = useStore();

    const submitting = ref(false);
    const formVisible = ref(false);
    const formMode = ref<AutoRechargeFormMode>('enable');
    const formError = ref<string | null>(null);
    const disableConfirmVisible = ref(false);

    const hasConfig = computed(() => (
      !!props.autoRecharge?.threshold && !!props.autoRecharge?.rechargeAmount
    ));

    const currency = computed<'EUR' | 'USD'>(
      () => (store.state.billing.overview?.shop?.currency === 'USD' ? 'USD' : 'EUR'),
    );
    const formatAmount = (n: number) => formatBillingAmount(n, currency.value);

    // ─── Toggle ──────────────────────────────────────────────────────────
    const onToggle = (next: boolean) => {
      if (submitting.value) return;
      if (next) {
        // Enable flow: always opens the form so the user confirms / sets
        // the values explicitly (even if previous values exist, we let them
        // review). Defaults seeded by the dialog when no value yet.
        formMode.value = 'enable';
        formError.value = null;
        formVisible.value = true;
      } else {
        // Disable flow: simple confirmation, then call setAutoRecharge.
        disableConfirmVisible.value = true;
      }
    };

    // ─── Edit ────────────────────────────────────────────────────────────
    const openEditForm = () => {
      if (submitting.value) return;
      formMode.value = 'edit';
      formError.value = null;
      formVisible.value = true;
    };

    // ─── Save (used by both enable and edit) ─────────────────────────────
    const onFormConfirm = async (v: AutoRechargeFormValue) => {
      submitting.value = true;
      formError.value = null;
      try {
        const result = await setWalletAutoRecharge({
          enabled: true,
          threshold: v.threshold,
          rechargeAmount: v.rechargeAmount,
          country: props.autoRecharge?.country ?? 'FR',
        });
        if (!result) {
          formError.value = t('billing.autoRechargeBlock.saveFailed');
          return;
        }
        await store.dispatch('billing/refetchOverview');
        formVisible.value = false;
      } catch {
        formError.value = t('billing.autoRechargeBlock.saveFailed');
      } finally {
        submitting.value = false;
      }
    };

    const onFormCancel = () => {
      formError.value = null;
    };

    // ─── Disable ─────────────────────────────────────────────────────────
    const onDisableConfirm = async () => {
      submitting.value = true;
      try {
        const result = await setWalletAutoRecharge({ enabled: false });
        if (!result) {
          showToastError(t('billing.autoRechargeBlock.saveFailed'));
          return;
        }
        await store.dispatch('billing/refetchOverview');
        disableConfirmVisible.value = false;
      } catch {
        showToastError(t('billing.autoRechargeBlock.saveFailed'));
      } finally {
        submitting.value = false;
      }
    };

    return {
      t,
      submitting,
      formVisible,
      formMode,
      formError,
      disableConfirmVisible,
      hasConfig,
      currency,
      formatAmount,
      onToggle,
      openEditForm,
      onFormConfirm,
      onFormCancel,
      onDisableConfirm,
    };
  },
});
</script>

<style lang="scss" scoped>
@import "@/assets/scss/utilities/variables";

.auto-recharge-block {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 18px 20px;
  transition: border-color 200ms ease, box-shadow 200ms ease;

  &--on {
    border-color: rgba(148, 200, 64, 0.4);
    box-shadow: 0 4px 16px -8px rgba(148, 200, 64, 0.32);
  }

  &__head {
    display: flex;
    align-items: center;
    gap: 14px;
  }

  &__icon {
    width: 40px;
    height: 40px;
    border-radius: 11px;
    background: rgba(148, 200, 64, 0.16);
    color: #629d03;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 1rem;
    flex-shrink: 0;
  }

  &__text {
    flex: 1;
    min-width: 0;
  }

  &__title {
    font-size: 0.9375rem;
    font-weight: 700;
    color: #0f172a;
    margin: 0;
    line-height: 1.2;
  }

  &__subtitle {
    font-size: 0.75rem;
    color: #64748b;
    margin: 3px 0 0;
    line-height: 1.45;
  }

  &__summary {
    margin-top: 14px;
    padding: 12px 14px;
    background: #f8faf3;
    border: 1px solid rgba(148, 200, 64, 0.25);
    border-radius: 10px;
    display: flex;
    align-items: center;
    gap: 10px;
  }

  &__summary-icon {
    color: #629d03;
    font-size: 0.9375rem;
    flex-shrink: 0;
  }

  &__summary-text {
    flex: 1;
    font-size: 0.8125rem;
    color: #2d5016;
    line-height: 1.4;
    font-weight: 500;
  }

  &__edit {
    flex-shrink: 0;
    background: #fff;
    border: 1px solid rgba(148, 200, 64, 0.3);
    color: #629d03;
    width: 30px;
    height: 30px;
    border-radius: 8px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 160ms ease;

    &:hover:not(:disabled) {
      background: rgba(148, 200, 64, 0.08);
      border-color: $brand-color-primary;
    }

    &:disabled { opacity: 0.5; cursor: not-allowed; }

    i { font-size: 0.8125rem; }
  }
}
</style>
